import { faUser } from "@fortawesome/pro-solid-svg-icons";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip, CircularIcon } from "@aviary";
import WellevateIcon from "@shared/assets/svg/graphics/wellevate-icon.svg";
import { useSharedGlobalConfig } from "@shared/hooks";
import { l } from "@shared/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome";

import * as styles from "./UserAvatar.styles";

interface Props {
  avatarImage?: string;
  firstName?: string;
  lastName?: string;
  label?: string;
  color?: keyof typeof AVATAR_COLORS;
  size?: keyof typeof AVATAR_SIZES;
  isImpersonating?: boolean;
  sourcePlatform?: string;
  isPatientData?: boolean;
  children?: ReactNode;
}

const AVATAR_SIZES = {
  tiny: "tiny",
  small: "small",
  medium: "medium",
  default: "default",
  large: "large",
  extraLarge: "extraLarge",
};

const AVATAR_COLORS = {
  grey: "grey",
  light: "light",
  white: "white",
};

const SOURCE_PLATFORM = {
  wellevate: "Wellevate",
};

const SOURCE_ICON = {
  wellevate: WellevateIcon,
};

/** @deprecated This component has an Aviary replacement, called Avatar! Please use it instead */
const UserAvatar = ({
  avatarImage,
  firstName,
  lastName,
  label,
  color,
  size,
  isImpersonating,
  children,
  sourcePlatform,
  isPatientData,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const renderGraphic = () => {
    if (avatarImage) {
      return <img src={avatarImage} css={styles.image} alt={label} aria-hidden={!label} />;
    } else if (firstName && lastName && firstName.length > 0 && lastName.length > 0) {
      const initials = firstName[0] + lastName[0];

      return <span css={styles.text}>{initials.toUpperCase()}</span>;
    } else {
      return (
        <span>
          <FontAwesomeIcon icon={faUser} />
        </span>
      );
    }
  };

  const conditionalGraphicStyles = [
    styles.graphic,
    size && styles.size[size],
    color && styles.color[color],
  ];

  const { isPatient: isUserPatient } = useSharedGlobalConfig();
  const showIcon = SOURCE_PLATFORM[sourcePlatform] && isPatientData && !isUserPatient;

  return (
    <span css={isImpersonating ? styles.impersonatingWrapper : undefined} {...rest}>
      <Tooltip
        tooltipContent={
          <p>{t(l.sharedCommon.createdIn, { sourcePlatform: SOURCE_PLATFORM[sourcePlatform] })}</p>
        }
        isTooltipHidden={!showIcon}
      >
        <span css={conditionalGraphicStyles} role="img" aria-label={label} aria-hidden={!label}>
          {renderGraphic()}
          {children}
        </span>
        {showIcon && (
          <CircularIcon
            size="xsmall"
            Svg={SOURCE_ICON[sourcePlatform]}
            css={styles.sourceIconPosition[size]}
            isColorPreserved
          />
        )}
      </Tooltip>
    </span>
  );
};

UserAvatar.defaultProps = {
  color: "white",
  size: "default",
};

export { UserAvatar, AVATAR_COLORS, AVATAR_SIZES };
